import { useCallback, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatDetail } from "@/lib/api";

/**
 * 149-cw-r153 — useSaveWithRetry.
 *
 * Hook untuk save operation dengan auto-retry dialog:
 * - Kalau saveFn throw → tampilkan dialog dgn pesan error jelas + tombol Coba Lagi + Batal.
 * - Klik "Coba Lagi" → re-invoke saveFn (closure retained), tutup dialog kalau sukses.
 * - Klik "Batal" → tutup dialog TAPI state parent (form yg diketik) TIDAK di-reset.
 *   User tidak kehilangan data — bisa salin/edit lalu Simpan lagi.
 * - Dialog berikan hint kontekstual berdasar status code:
 *     403 → arahkan ke Muat Ulang banner (access cache stale)
 *     ≥500 / network → cek koneksi
 *
 * Cara pakai:
 *   const { runSave, RetryDialog, busy } = useSaveWithRetry();
 *   const submit = (e) => {
 *     e.preventDefault();
 *     runSave(async () => {
 *       await api.post(...);
 *       toast.success("Tersimpan");
 *       onOpenChange(false);
 *     });
 *   };
 *   return (
 *     <form onSubmit={submit}>
 *       ...
 *       <button disabled={busy}>Simpan</button>
 *       <RetryDialog />
 *     </form>
 *   );
 */
export function useSaveWithRetry() {
  const [dialog, setDialog] = useState(null); // { fn, error, status }
  const [busy, setBusy] = useState(false);

  const runSave = useCallback(async (fn) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      setDialog(null);
    } catch (err) {
      const detail = formatDetail(err?.response?.data?.detail) || err?.message || "Penyimpanan gagal — tidak diketahui.";
      setDialog({ fn, error: detail, status: err?.response?.status });
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const retry = useCallback(async () => {
    if (!dialog?.fn) return;
    setBusy(true);
    try {
      await dialog.fn();
      setDialog(null);
    } catch (err) {
      const detail = formatDetail(err?.response?.data?.detail) || err?.message || "Penyimpanan masih gagal.";
      setDialog((d) => (d ? { ...d, error: detail, status: err?.response?.status } : d));
    } finally {
      setBusy(false);
    }
  }, [dialog]);

  const cancel = useCallback(() => setDialog(null), []);

  const RetryDialog = useCallback(() => (
    <Dialog open={!!dialog} onOpenChange={(o) => { if (!o && !busy) cancel(); }}>
      <DialogContent className="rounded-none max-w-md" data-testid="save-retry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight inline-flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" /> Gagal Menyimpan
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900">
            <div className="font-semibold mb-1">Data belum tersimpan.</div>
            <div className="text-[11px]" data-testid="save-retry-error">{dialog?.error}</div>
            {dialog?.status === 403 && (
              <div className="text-[10px] text-amber-800 mt-2 italic">
                Akses ditolak — kemungkinan permission belum termuat. Coba klik <b>Muat Ulang</b> di banner atas, lalu Coba Lagi.
              </div>
            )}
            {(dialog?.status === undefined || dialog?.status === 0 || (dialog?.status && dialog.status >= 500)) && (
              <div className="text-[10px] text-amber-800 mt-2 italic">
                Server tidak merespons / error internal. Cek koneksi internet Anda, lalu coba lagi.
              </div>
            )}
          </div>
          <div className="bg-emerald-50 border border-emerald-200 p-3 text-[11px] text-emerald-800">
            <b>Data yang sudah Anda isi tidak hilang.</b> Klik <b>Batal</b> untuk kembali ke form dan ubah/salin data, atau <b>Coba Lagi</b> untuk mengulang penyimpanan.
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={cancel}
            disabled={busy}
            data-testid="save-retry-cancel"
          >
            Batal · Kembali ke Form
          </Button>
          <Button
            data-testid="save-retry-confirm"
            onClick={retry}
            disabled={busy}
            className="rounded-none bg-[#0B1D3A] hover:bg-slate-800 text-white inline-flex items-center gap-2"
          >
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> {busy ? "Mencoba…" : "Coba Lagi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ), [dialog, busy, cancel, retry]);

  return { runSave, RetryDialog, busy };
}
